import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { validateAudioFile, formatFileSize, createFormData } from '../utils/fileUpload';
import './AdminProductManager.css';

const AdminProductManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other',
    productType: 'music_album',
    genre: '',
    price: '',
    paymentCurrency: 'USDT',
    currency: 'USD',
    image: '',
    isAvailable: true,
    isFeatured: false
  });
  const [productFiles, setProductFiles] = useState([]);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [existingPreviews, setExistingPreviews] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [totalPages, setTotalPages] = useState(1);

  const getPageItems = (current, total) => {
    if (total <= 1) return [1];
    const items = [];
    const windowSize = 2;
    const start = Math.max(2, current - windowSize);
    const end = Math.min(total - 1, current + windowSize);

    items.push(1);
    if (start > 2) items.push('ellipsis-left');
    for (let i = start; i <= end; i += 1) {
      items.push(i);
    }
    if (end < total - 1) items.push('ellipsis-right');
    items.push(total);
    return items;
  };

  const fetchProducts = useCallback(async (pageToLoad = 1) => {
    try {
      setLoading(true);
      const response = await api.get(`/products?page=${pageToLoad}&limit=${limit}`);
      setProducts(response.data.products || []);
      setTotalPages(response.data.pagination?.pages || 1);
      if (pageToLoad > (response.data.pagination?.pages || 1)) {
        setPage(1);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
      setProducts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchProducts(page);
  }, [page, fetchProducts]);

  const handleAdd = () => {
    setEditingProduct(null);
    setFormData({
      title: '',
      description: '',
      category: 'music_album',
      productType: 'music_album',
      genre: '',
      price: '',
      paymentCurrency: 'USDT',
      currency: 'USD',
      image: '',
      isAvailable: true,
      isFeatured: false
    });
    setProductFiles([]);
    setPreviewFiles([]);
    setExistingFiles([]);
    setExistingPreviews([]);
    setShowAddForm(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product._id);
    setFormData({
      title: product.title || '',
      description: product.description || '',
      category: product.category || 'other',
      productType: product.productType || 'music_album',
      genre: product.genre || '',
      price: product.price || '',
      paymentCurrency: product.paymentCurrency || 'USDT',
      currency: product.currency || 'USD',
      image: product.image || '',
      isAvailable: product.isAvailable !== undefined ? product.isAvailable : true,
      isFeatured: product.isFeatured || false
    });
    setProductFiles([]);
    setPreviewFiles([]);
    setExistingFiles(product.files || []);
    setExistingPreviews(product.previewFiles || []);
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingProduct(null);
    setFormData({
      title: '',
      description: '',
      category: 'other',
      productType: 'music_album',
      price: '',
      paymentCurrency: 'USDT',
      currency: 'USD',
      image: '',
      isAvailable: true,
      isFeatured: false
    });
    setProductFiles([]);
    setPreviewFiles([]);
    setExistingFiles([]);
    setExistingPreviews([]);
  };

  const handleFileChange = (e, isPreview = false) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (!validateAudioFile(file)) {
        toast.error(`${file.name} is not a valid audio file`);
        return false;
      }
      return true;
    });

    if (isPreview) {
      setPreviewFiles([...previewFiles, ...validFiles]);
    } else {
      setProductFiles([...productFiles, ...validFiles]);
    }
  };

  const removeFile = (index, isPreview = false) => {
    if (isPreview) {
      setPreviewFiles(previewFiles.filter((_, i) => i !== index));
    } else {
      setProductFiles(productFiles.filter((_, i) => i !== index));
    }
  };

  const removeExistingFile = (fileId, isPreview = false) => {
    if (isPreview) {
      setExistingPreviews(existingPreviews.filter(f => f._id.toString() !== fileId));
    } else {
      setExistingFiles(existingFiles.filter(f => f._id.toString() !== fileId));
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.title || !formData.description || !formData.price) {
        toast.error('Please fill in all required fields');
        return;
      }

      if (productFiles.length === 0 && existingFiles.length === 0 && !editingProduct) {
        toast.error('Please upload at least one product file');
        return;
      }

      setUploading(true);

      // Create FormData for file upload (only product files, not previews)
      const formDataToSend = createFormData(formData, productFiles, []);

      let productId;
      if (editingProduct) {
        // Update existing product
        await api.put(`/products/${editingProduct}`, formDataToSend);
        productId = editingProduct;
        toast.success('Product updated successfully!');
      } else {
        // Create new product
        const response = await api.post('/products', formDataToSend);
        productId = response.data.product._id;
        toast.success('Product created successfully!');
      }

      // Upload preview files separately if any
      if (previewFiles.length > 0 && productId) {
        try {
          const previewFormData = new FormData();
          previewFiles.forEach((file) => {
            previewFormData.append('previews', file);
          });
          
          await api.post(`/products/${productId}/previews`, previewFormData);
          toast.success('Preview files uploaded successfully!');
        } catch (previewError) {
          console.error('Error uploading previews:', previewError);
          toast.warning('Product saved but preview upload failed. You can add previews later by editing the product.');
        }
      }

      setShowAddForm(false);
      setEditingProduct(null);
      setProductFiles([]);
      setPreviewFiles([]);
      setExistingFiles([]);
      setExistingPreviews([]);
      setPage(1);
      fetchProducts(1);
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.message || 'Failed to save product');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await api.delete(`/products/${id}`);
      toast.success('Product deleted successfully!');
      fetchProducts(page);
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  if (loading) {
    return <div className="loading">Loading products...</div>;
  }

  return (
    <div className="admin-product-manager">
      <div className="product-manager-header">
        <h2>Product Management</h2>
        <button className="btn btn-primary" onClick={handleAdd}>
          Add New Product
        </button>
      </div>

      {showAddForm && (
        <div className="product-form-modal">
          <div className="product-form" style={{ maxWidth: '800px' }}>
            <h3>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="form-input"
                  placeholder="e.g., AI Generated Album Vol. 1"
                />
              </div>

              <div className="form-group">
                <label>Product Type *</label>
                <select
                  value={formData.productType}
                  onChange={(e) => setFormData({ ...formData, productType: e.target.value })}
                  className="form-input"
                >
                  <option value="music_album">Music Album</option>
                  <option value="single_track">Single Track</option>
                  <option value="beat">Beat</option>
                  <option value="sample_pack">Sample Pack</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="form-input"
                >
                  <option value="music_album">Music Album</option>
                  <option value="song_catalog">Song Catalog</option>
                  <option value="beat">Beat</option>
                  <option value="instrumental">Instrumental</option>
                  <option value="sample_pack">Sample Pack</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {(formData.category === 'music_album' || formData.category === 'song_catalog') && (
                <div className="form-group">
                  <label>Genre</label>
                  <select
                    value={formData.genre}
                    onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                    className="form-input"
                  >
                    <option value="">Select Genre</option>
                    <option value="Pop">Pop</option>
                    <option value="Rock">Rock</option>
                    <option value="Hip-Hop">Hip-Hop</option>
                    <option value="R&B">R&B</option>
                    <option value="Electronic">Electronic</option>
                    <option value="Jazz">Jazz</option>
                    <option value="Country">Country</option>
                    <option value="Reggae">Reggae</option>
                    <option value="Latin">Latin</option>
                    <option value="Classical">Classical</option>
                    <option value="Metal">Metal</option>
                    <option value="Folk">Folk</option>
                    <option value="Blues">Blues</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Price *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="form-input"
                  placeholder="e.g., 10.00"
                />
              </div>

              <div className="form-group">
                <label>Payment Currency *</label>
                <select
                  value={formData.paymentCurrency}
                  onChange={(e) => setFormData({ ...formData, paymentCurrency: e.target.value })}
                  className="form-input"
                >
                  <option value="USDT">USDT</option>
                  <option value="PHONE">PHONE Token</option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isAvailable}
                    onChange={(e) => setFormData({ ...formData, isAvailable: e.target.checked })}
                  />
                  Available for purchase
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="form-textarea"
                rows="4"
                placeholder="Describe your product..."
              />
            </div>

            <div className="form-group">
              <label>Main Image URL</label>
              <input
                type="url"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                className="form-input"
                placeholder="https://..."
              />
            </div>

            {/* Product Files Upload */}
            <div className="form-group">
              <label>Product Files (Music Tracks) *</label>
              <input
                type="file"
                multiple
                accept="audio/*"
                onChange={(e) => handleFileChange(e, false)}
                className="form-input"
              />
              <small style={{ color: '#888', marginTop: '0.5rem', display: 'block' }}>
                Upload all tracks for your album/product. Supported: MP3, WAV, M4A, FLAC, OGG, AAC
              </small>
              
              {/* Existing Files */}
              {existingFiles.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <strong style={{ color: '#00e0ff' }}>Existing Files:</strong>
                  {existingFiles.map((file, index) => (
                    <div key={file._id || index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '0.5rem',
                      background: 'rgba(0, 224, 255, 0.1)',
                      borderRadius: '4px',
                      marginTop: '0.5rem'
                    }}>
                      <span style={{ color: '#fff', fontSize: '0.875rem' }}>
                        {file.originalName || file.filename} ({formatFileSize(file.fileSize || 0)})
                      </span>
                      <button
                        type="button"
                        onClick={() => removeExistingFile(file._id, false)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #ef4444',
                          color: '#ef4444',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* New Files */}
              {productFiles.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <strong style={{ color: '#00e0ff' }}>New Files to Upload:</strong>
                  {productFiles.map((file, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '0.5rem',
                      background: 'rgba(0, 224, 255, 0.1)',
                      borderRadius: '4px',
                      marginTop: '0.5rem'
                    }}>
                      <span style={{ color: '#fff', fontSize: '0.875rem' }}>
                        {file.name} ({formatFileSize(file.size)})
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(index, false)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #ef4444',
                          color: '#ef4444',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Preview Files Upload */}
            <div className="form-group">
              <label>Preview Files (30-second previews)</label>
              <input
                type="file"
                multiple
                accept="audio/*"
                onChange={(e) => handleFileChange(e, true)}
                className="form-input"
              />
              <small style={{ color: '#888', marginTop: '0.5rem', display: 'block' }}>
                Optional: Upload 30-second preview files for each track. Users can preview before purchase.
              </small>
              
              {/* Existing Previews */}
              {existingPreviews.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <strong style={{ color: '#00e0ff' }}>Existing Previews:</strong>
                  {existingPreviews.map((file, index) => (
                    <div key={file._id || index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '0.5rem',
                      background: 'rgba(0, 224, 255, 0.1)',
                      borderRadius: '4px',
                      marginTop: '0.5rem'
                    }}>
                      <span style={{ color: '#fff', fontSize: '0.875rem' }}>
                        {file.originalName || file.filename}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeExistingFile(file._id, true)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #ef4444',
                          color: '#ef4444',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* New Previews */}
              {previewFiles.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <strong style={{ color: '#00e0ff' }}>New Previews to Upload:</strong>
                  {previewFiles.map((file, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '0.5rem',
                      background: 'rgba(0, 224, 255, 0.1)',
                      borderRadius: '4px',
                      marginTop: '0.5rem'
                    }}>
                      <span style={{ color: '#fff', fontSize: '0.875rem' }}>
                        {file.name} ({formatFileSize(file.size)})
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(index, true)}
                        style={{
                          background: 'transparent',
                          border: '1px solid #ef4444',
                          color: '#ef4444',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isFeatured}
                  onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                />
                Featured product
              </label>
            </div>

            <div className="form-actions">
              <button className="btn btn-secondary" onClick={handleCancel} disabled={uploading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={uploading}>
                {uploading ? 'Uploading...' : editingProduct ? 'Update Product' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="products-list">
        {products.length === 0 ? (
          <div className="empty-state">
            <p>No products yet. Click "Add New Product" to create one.</p>
          </div>
        ) : (
          <div className="products-grid">
            {products.map((product) => (
              <div key={product._id} className="product-card">
                {product.image && (
                  <div className="product-image">
                    <img src={product.image} alt={product.title} />
                    {product.isFeatured && <span className="featured-badge">Featured</span>}
                  </div>
                )}
                <div className="product-info">
                  <h4>{product.title}</h4>
                  <p className="product-category">{product.category.replace('_', ' ')}</p>
                  <p className="product-price">
                    {product.price} {product.paymentCurrency || product.currency}
                    {product.files && product.files.length > 0 && (
                      <span style={{ fontSize: '0.75rem', color: '#888', marginLeft: '0.5rem' }}>
                        ({product.files.length} {product.files.length === 1 ? 'file' : 'files'})
                      </span>
                    )}
                  </p>
                  <div className="product-status">
                    <span className={product.isAvailable ? 'status-available' : 'status-unavailable'}>
                      {product.isAvailable ? 'Available' : 'Unavailable'}
                    </span>
                  </div>
                  <div className="product-actions">
                    <button
                      className="btn btn-small btn-primary"
                      onClick={() => handleEdit(product)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={() => handleDelete(product._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-secondary"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <div className="pagination-numbers">
            {getPageItems(page, totalPages).map((item) => {
              if (typeof item !== 'number') {
                return (
                  <span key={item} className="pagination-ellipsis">…</span>
                );
              }
              return (
                <button
                  key={item}
                  className={`pagination-page ${item === page ? 'active' : ''}`}
                  onClick={() => setPage(item)}
                  disabled={item === page}
                >
                  {item}
                </button>
              );
            })}
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminProductManager;
